import { useRef } from 'react';

import useNotify from '@hooks/notify';

import CustomTableHeader from './CustomTableHeader';
import CustomTablePagination from './CustomtablePagination';
import CustomTableRow from './CustomTableRow';
import {
  CustomTableHeaderProps,
  CustomTableRowData,
  CustomTableSortOrder,
  CustomTableSortState
} from './types';

interface CustomTableProps<T extends string | number = string> {
  headers: CustomTableHeaderProps[];
  data: CustomTableRowData<T>[];
  enableSort?: boolean;
  sortState?: CustomTableSortState | null;
  onSortChange?: (sortKey: string, sortOrder: CustomTableSortOrder) => void;
  handleSelect?: (row: CustomTableRowData<T>) => void;
  totalCount?: number;
  onPageChange?: (count: number) => void;
  currentPage?: number;
  hasMultiSelect?: boolean;
  handleSelectAll?: (selectedIds: T[], removedIds: T[]) => void;
  selectedRows?: T[];
  handleCheckboxSelect?: (row: CustomTableRowData<T>) => void;
  maxLimit?: number;
  disabledRowIds?: T[];
  disabledRowTooltips?: Partial<Record<T, string>>;
  checkboxDisabledRowIds?: T[];
  hasInfiniteScroll?: boolean;
  className?: string;
  customRowClassName?: string;
  disableSelectedHighlight?: boolean;
  paginationLimit?: number;
  showGoToPage?: boolean;
  minWidth?: number;
}

const CustomTable = <T extends string | number = string>({
  headers,
  data,
  handleSelect,
  totalCount,
  onPageChange,
  currentPage,
  hasMultiSelect,
  handleSelectAll,
  selectedRows,
  handleCheckboxSelect,
  maxLimit,
  disabledRowIds,
  disabledRowTooltips,
  checkboxDisabledRowIds,
  hasInfiniteScroll = false,
  className = '',
  customRowClassName = '',
  disableSelectedHighlight = false,
  enableSort = false,
  sortState = null,
  onSortChange,
  paginationLimit,
  showGoToPage = false,
  minWidth
}: CustomTableProps<T>) => {
  const { createErrorAlert } = useNotify();

  const infiniteScrollRef = useRef<HTMLDivElement>(null);

  const showPagination = totalCount && totalCount > 0;

  const isCheckboxDisabled = (id: T) =>
    disabledRowIds?.includes(id) || checkboxDisabledRowIds?.includes(id);

  const isSelectionLimitReached = (itemCount = 1) =>
    maxLimit && selectedRows && selectedRows.length + itemCount > maxLimit;

  const handleSelectAllCheckbox = (checked: boolean) => {
    const filteredData = checked
      ? data
          .filter((item) => !selectedRows?.includes(item.id))
          .filter((item) => !isCheckboxDisabled(item.id))
      : data
          .filter((item) => selectedRows?.includes(item.id))
          .filter((item) => !isCheckboxDisabled(item.id));

    if (checked) {
      if (isSelectionLimitReached(filteredData.length)) {
        createErrorAlert('Limit reached');
        return;
      }
      handleSelectAll?.(
        filteredData.map((item) => item.id),
        []
      );
    } else {
      handleSelectAll?.(
        [],
        filteredData.map((item) => item.id)
      );
    }
  };

  const handleCheckboxSelectCustom = (row: CustomTableRowData<T>) => {
    if (isCheckboxDisabled(row.id)) return;
    if (isSelectionLimitReached() && !selectedRows?.includes(row.id))
      createErrorAlert('Limit reached');
    else handleCheckboxSelect?.(row);
  };

  const handleSelectCustom = (row: CustomTableRowData<T>) => {
    if (!handleSelect) return;
    if (disabledRowIds?.includes(row.id)) return;
    if (isSelectionLimitReached() && !selectedRows?.includes(row.id))
      createErrorAlert('Limit reached');
    else handleSelect(row);
  };

  const isAllRowSelected = () =>
    data
      .filter((item) => !isCheckboxDisabled(item.id))
      .every((item) => selectedRows?.includes(item.id));

  const isAllRowDisabled = () =>
    data.every((item) => isCheckboxDisabled(item.id));

  const tableBody = (
    <>
      <CustomTableHeader
        headers={headers}
        hasMultiSelect={hasMultiSelect}
        handleSelectAll={handleSelectAllCheckbox}
        isAllRowSelected={isAllRowSelected()}
        isAllRowDisabled={isAllRowDisabled()}
        enableSort={enableSort}
        sortState={sortState}
        onSortChange={onSortChange}
      />
      <CustomTableRow<T>
        headers={headers}
        data={data}
        handleSelect={handleSelectCustom}
        hasRowClickHandler={Boolean(handleSelect)}
        hasMultiSelect={hasMultiSelect}
        selectedRows={selectedRows}
        disabledRowIds={disabledRowIds}
        disabledRowTooltips={disabledRowTooltips}
        checkboxDisabledRowIds={checkboxDisabledRowIds}
        handleCheckboxSelect={handleCheckboxSelectCustom}
        customClassName={customRowClassName}
        disableSelectedHighlight={disableSelectedHighlight}
      />
    </>
  );

  return (
    <div className={`${showPagination ? 'pb-4' : 'pb-18'} ${className}`}>
      {minWidth ? (
        <div className="w-full overflow-x-auto">
          <div style={{ minWidth }}>{tableBody}</div>
        </div>
      ) : (
        tableBody
      )}
      {showPagination &&
        (hasInfiniteScroll ? (
          <div ref={infiniteScrollRef} className="h-1" />
        ) : (
          <CustomTablePagination
            currentPage={currentPage || 1}
            totalCount={totalCount || 0}
            onPageChange={onPageChange || (() => {})}
            isFixed={false}
            paginationLimit={paginationLimit}
            showGoToPage={showGoToPage}
          />
        ))}
    </div>
  );
};

export default CustomTable;
